import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Expertise from './components/Expertise';
import Approach from './components/Approach';
import Work from './components/Work';
import People from './components/People';
import Insights from './components/Insights';
import Contact from './components/Contact';
import Footer from './components/Footer';
import { scrollToSection } from '@/components/feature/SmartLink';

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const mainRef = useRef<HTMLElement>(null);
  const { hash, state, key } = useLocation();

  // Cross-route anchors, direct access, refresh and back/forward: jump once pin spacers exist.
  useEffect(() => {
    if ((state as { inPage?: boolean } | null)?.inPage) return undefined;
    // Top-of-page is owned by ScrollManager.
    if (!hash) return undefined;
    const id = hash.slice(1);
    const timers: number[] = [];
    let tries = 0;
    const attempt = () => {
      tries += 1;
      if (!scrollToSection(id, false) && tries < 20) { timers.push(window.setTimeout(attempt, 100)); return; }
      // re-settle once late images / pin layout finish
      if (tries < 20) timers.push(window.setTimeout(() => scrollToSection(id, false), 450));
    };
    timers.push(window.setTimeout(attempt, 120));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [hash, key, state]);

  useLayoutEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>('[data-reveal]').forEach((el) => {
        gsap.from(el, { y: 18, opacity: 0, duration: 0.8, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 88%', once: true } });
      });
      gsap.utils.toArray<HTMLElement>('[data-clip]').forEach((el) => {
        gsap.from(el, { clipPath: 'inset(12% 0% 12% 0%)', duration: 0.9, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
      });
    }, mainRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <Navbar />
      <main ref={mainRef}>
        <Hero />
        <About />
        <Expertise />
        <Approach />
        <Work />
        <People />
        <Insights />
        <Contact />
      </main>
      <Footer />
    </>
  );
}